/**
 * Shared plumbing for "live job rows" endpoints — a query + subscription pair that reads back
 * pending/claimed `exam_jobs` rows of given types as plain JSON strings, for a canvas box to derive its
 * loading-spinner state from. `get-live-grading-jobs.ts` and `get-live-solution-jobs.ts` are both this
 * shape; this file factors out the filter/mapping/watchQuery wiring they'd otherwise duplicate.
 *
 * Deliberately NOT a `registerQuery`/`registerSubscription` factory: those calls stay as literal,
 * inline statements in each concrete file (with their own literal `operationName`), so nothing about the
 * baseql registration surface becomes dynamic — only the logic around it is shared.
 */
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { defer, Observable, switchMap } from 'rxjs';
import { loadAndAssert } from '../../authz/access.js';
import { Exam } from '../../exam.js';
import { EXAM_JOBS_COLLECTION } from './job.js';

const LIVE_STATUSES = ['pending', 'claimed'] as const;

export function liveJobFilter(examId: string, types: readonly string[]): Record<string, unknown> {
  return { examId, type: { $in: [...types] }, status: { $in: [...LIVE_STATUSES] } };
}

/** Maps docs to rows via `toRow` (return `null` to drop a doc), JSON-stringified for the wire. */
export function toJsonRows<Row>(docs: any[], toRow: (doc: any) => Row | null): string[] {
  return docs
    .map(toRow)
    .filter((row): row is Row => row !== null)
    .map(row => JSON.stringify(row));
}

export async function queryLiveJobRows<Row>(
  examId: string,
  types: readonly string[],
  toRow: (doc: any) => Row | null,
): Promise<string[]> {
  // `exam_jobs` is a raw, unfiltered collection keyed by examId — the exam decides who may see its rows.
  await loadAndAssert(Exam, examId, 'read', { _id: 1 });
  const collection = await getRawCollection(EXAM_JOBS_COLLECTION);
  const docs = await collection.find(liveJobFilter(examId, types)).toArray();
  return toJsonRows(docs, toRow);
}

export function watchLiveJobRows<Row>(
  examId: string,
  types: readonly string[],
  toRow: (doc: any) => Row | null,
): Observable<string[]> {
  return new Observable<string[]>(subscriber => {
    const sub = watchQuery(EXAM_JOBS_COLLECTION, liveJobFilter(examId, types)).subscribe({
      next: rows => subscriber.next(toJsonRows(Array.isArray(rows) ? rows : [], toRow)),
      error: err => subscriber.error(err),
      complete: () => subscriber.complete(),
    });

    return () => {
      sub.unsubscribe();
    };
    // Same check as `queryLiveJobRows`, once on subscribe, before the unfiltered watch starts.
  }).pipe(source => defer(() => loadAndAssert(Exam, examId, 'read', { _id: 1 })).pipe(switchMap(() => source)));
}
